import { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { supabase } from '@/integrations/supabase/client';
import type { FocusMode, FOCUS_MODE_TEMPLATES } from '@/types/notifications';
import { FOCUS_MODE_TEMPLATES as TEMPLATES } from '@/types/notifications';

/**
 * Hook for managing Focus Modes
 * 
 * @example
 * ```typescript
 * const { activeFocusMode, activate, deactivate, isActive } = useFocusMode();
 * 
 * // Activate Do Not Disturb for 2 hours
 * await activate('do-not-disturb-id', 120);
 * 
 * // Deactivate current mode
 * await deactivate();
 * ```
 */
export function useFocusMode() {
  const { user } = useUser();
  const [focusModes, setFocusModes] = useState<FocusMode[]>([]);
  const [activeFocusMode, setActiveFocusMode] = useState<FocusMode | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  // Fetch focus modes for the user
  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    fetchFocusModes();
    
    // Subscribe to focus mode changes
    const channel = supabase
      .channel('focus-modes-realtime')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'focus_modes',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        console.log('[useFocusMode] Realtime event:', payload);
        fetchFocusModes();
      })
      .subscribe((status) => {
        console.log('[useFocusMode] Subscription status:', status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const fetchFocusModes = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('focus_modes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const modes = (data || []) as FocusMode[];
      setFocusModes(modes);

      // Find active mode
      const active = modes.find(m => m.is_active);
      setActiveFocusMode(active);
    } catch (err: any) {
      console.error('Error fetching focus modes:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Activate a focus mode
   * @param modeId - ID of the focus mode to activate
   * @param durationMinutes - Optional duration in minutes (auto-deactivate after this time)
   */
  const activate = async (modeId: string, durationMinutes?: number) => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    try {
      // Find the mode being activated for immediate UI update
      const modeToActivate = focusModes.find(m => m.id === modeId);
      
      // First, deactivate all other focus modes for this user
      await supabase
        .from('focus_modes')
        .update({ 
          is_active: false,
          deactivated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .eq('is_active', true);

      // Calculate deactivation time if duration is provided
      const deactivateAt = durationMinutes 
        ? new Date(Date.now() + durationMinutes * 60 * 1000).toISOString()
        : null;

      // Now activate the selected mode
      const { error } = await supabase
        .from('focus_modes')
        .update({ 
          is_active: true,
          activated_at: new Date().toISOString(),
          ...(deactivateAt && { deactivated_at: deactivateAt })
        })
        .eq('id', modeId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Immediately update local state for instant UI feedback
      if (modeToActivate) {
        const activeMode = { ...modeToActivate, is_active: true };
        setActiveFocusMode(activeMode);
        
        // Dispatch event for other components to update immediately
        window.dispatchEvent(new CustomEvent('focus-mode-changed', { 
          detail: { active: true, mode: activeMode } 
        }));
      }

      // If duration is set, schedule auto-deactivation
      if (durationMinutes) {
        setTimeout(() => {
          deactivate();
        }, durationMinutes * 60 * 1000);
      }

      // Refresh focus modes list (will sync with DB)
      await fetchFocusModes();
    } catch (err: any) {
      console.error('Error activating focus mode:', err);
      throw err;
    }
  };

  /**
   * Deactivate the currently active focus mode
   */
  const deactivate = async () => {
    if (!user?.id || !activeFocusMode) return;

    try {
      // Immediately update local state for instant UI feedback
      setActiveFocusMode(undefined);
      
      // Dispatch event for other components to update immediately
      window.dispatchEvent(new CustomEvent('focus-mode-changed', { 
        detail: { active: false, mode: null } 
      }));
      
      const { error } = await supabase
        .from('focus_modes')
        .update({ 
          is_active: false,
          deactivated_at: new Date().toISOString()
        })
        .eq('id', activeFocusMode.id)
        .eq('user_id', user.id);

      if (error) {
        // Revert on error
        setActiveFocusMode(activeFocusMode);
        window.dispatchEvent(new CustomEvent('focus-mode-changed', { 
          detail: { active: true, mode: activeFocusMode } 
        }));
        throw error;
      }

      // Still fetch to sync, but UI already updated
      await fetchFocusModes();
    } catch (err: any) {
      console.error('Error deactivating focus mode:', err);
      throw err;
    }
  };

  /**
   * Create a new focus mode from a template
   */
  const createFromTemplate = async (templateName: string, organizationId?: string) => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    const template = TEMPLATES.find(t => t.name === templateName);
    if (!template) {
      throw new Error(`Template "${templateName}" not found`);
    }

    try {
      // Fetch channels that match the template's allowed types
      let allowedChannelIds: string[] = [];
      
      if (template.allowed_channel_types && template.allowed_channel_types.length > 0) {
        const { data: channels } = await supabase
          .from('notification_channels')
          .select('id')
          .in('name', template.allowed_channel_types);
        
        allowedChannelIds = (channels || []).map(c => c.id);
      }

      const { data, error } = await supabase
        .from('focus_modes')
        .insert({
          user_id: user.id,
          organization_id: organizationId,
          name: template.name,
          icon_name: template.icon_name,
          color: template.color,
          do_not_disturb: template.do_not_disturb,
          allowed_channels: allowedChannelIds,
          is_system: true, // Mark template-based modes as system modes
        })
        .select()
        .single();

      if (error) throw error;

      await fetchFocusModes();
      return data as FocusMode;
    } catch (err: any) {
      console.error('Error creating focus mode from template:', err);
      throw err;
    }
  };

  /**
   * Create a custom focus mode
   */
  const createMode = async (config: Partial<FocusMode>) => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    try {
      const { data, error } = await supabase
        .from('focus_modes')
        .insert({
          user_id: user.id,
          organization_id: config.organization_id,
          name: config.name || 'Custom Mode',
          icon_name: config.icon_name || 'Settings',
          color: config.color || '#6b7280',
          do_not_disturb: config.do_not_disturb || false,
          allowed_channels: config.allowed_channels || [],
          blocked_channels: config.blocked_channels || [],
          active_times: config.active_times || [],
          auto_reply_message: config.auto_reply_message,
          is_system: false,
        })
        .select()
        .single();

      if (error) throw error;

      await fetchFocusModes();
      return data as FocusMode;
    } catch (err: any) {
      console.error('Error creating custom focus mode:', err);
      throw err;
    }
  };

  /**
   * Update an existing focus mode
   */
  const updateMode = async (modeId: string, config: Partial<FocusMode>) => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    try {
      const { error } = await supabase
        .from('focus_modes')
        .update({
          name: config.name,
          icon_name: config.icon_name,
          color: config.color,
          do_not_disturb: config.do_not_disturb,
          allowed_channels: config.allowed_channels,
          blocked_channels: config.blocked_channels,
          active_times: config.active_times,
          auto_reply_message: config.auto_reply_message,
        })
        .eq('id', modeId)
        .eq('user_id', user.id);

      if (error) throw error;

      await fetchFocusModes();
    } catch (err: any) {
      console.error('Error updating focus mode:', err);
      throw err;
    }
  };

  /**
   * Delete a focus mode (only non-system modes)
   */
  const deleteMode = async (modeId: string) => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    try {
      const mode = focusModes.find(m => m.id === modeId);
      if (mode?.is_system) {
        throw new Error('Cannot delete system focus modes');
      }

      const { error } = await supabase
        .from('focus_modes')
        .delete()
        .eq('id', modeId)
        .eq('user_id', user.id);

      if (error) throw error;

      await fetchFocusModes();
    } catch (err: any) {
      console.error('Error deleting focus mode:', err);
      throw err;
    }
  };

  /**
   * Get available focus mode templates
   */
  const getTemplates = () => TEMPLATES;

  return {
    // State
    focusModes,
    activeFocusMode,
    isActive: !!activeFocusMode,
    loading,
    error,
    
    // Actions
    activate,
    deactivate,
    createFromTemplate,
    createMode,
    updateMode,
    deleteMode,
    
    // Helpers
    getTemplates,
    refetch: fetchFocusModes,
  };
}
